import React, { createContext, useContext, useState, useEffect } from "react";
import "./Tabs.css";

type TabsContextType = {
  value: string;
  onValueChange: (value: string) => void;
};

const TabsContext = createContext<TabsContextType | undefined>(undefined);

export type TabsProps = {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children?: React.ReactNode;
};

export const Tabs = ({
  defaultValue = "",
  value,
  onValueChange,
  className = "",
  children,
  ...props
}: TabsProps & React.ComponentPropsWithoutRef<"div">) => {
  const [internalValue, setInternalValue] = useState(defaultValue ?? "");

  useEffect(() => {
    if (defaultValue !== undefined) {
      setInternalValue(defaultValue);
    }
  }, [defaultValue]);

  const currentValue = value ?? internalValue ?? "";

  const handleValueChange = (nextValue: string) => {
    if (value === undefined) {
      setInternalValue(nextValue);
    }
    onValueChange?.(nextValue);
  };

  return (
    <TabsContext.Provider value={{ value: currentValue, onValueChange: handleValueChange }}>
      <div 
        className={`ds2-tabs ${className}`}
        {...props}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
};

export type TabsListProps = {
  className?: string;
  children?: React.ReactNode;
};

export const TabsList = ({
  className = "",
  children,
  ...props
}: TabsListProps & React.ComponentPropsWithoutRef<"div">) => {
  return (
    <div 
      className={`ds2-tabs-list ${className}`}
      role="tablist"
      {...props}
    >
      {children}
    </div>
  );
};

export type TabsTriggerProps = {
  value: string;
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
};

export const TabsTrigger = ({
  value: triggerValue,
  className = "",
  children,
  disabled = false,
  ...props
}: TabsTriggerProps & React.ComponentPropsWithoutRef<"button">) => {
  const context = useContext(TabsContext);
  
  if (!context) {
    throw new Error("TabsTrigger must be used within a Tabs component");
  }

  const { value, onValueChange } = context;
  const isActive = value === triggerValue;

  return (
    <button
      className={`ds2-tabs-trigger ${isActive ? "ds2-tabs-trigger-active" : ""} ${className}`}
      role="tab"
      aria-selected={isActive}
      disabled={disabled}
      onClick={() => onValueChange(triggerValue)}
      {...props}
    >
      {children}
    </button>
  );
};

export type TabsContentProps = {
  value: string;
  className?: string;
  children?: React.ReactNode;
};

export const TabsContent = ({
  value: contentValue,
  className = "",
  children,
  ...props
}: TabsContentProps & React.ComponentPropsWithoutRef<"div">) => {
  const context = useContext(TabsContext);
  
  if (!context) {
    throw new Error("TabsContent must be used within a Tabs component");
  }

  const { value } = context;
  const isActive = value === contentValue;

  if (!isActive) {
    return null;
  }

  return (
    <div 
      className={`ds2-tabs-content ${className}`}
      role="tabpanel"
      {...props}
    >
      {children}
    </div>
  );
};
